import { useState } from "react"
import { CompanyEntity } from "../../Objects/Company/CompanyEntity"
import { AddressEntity } from "../../Objects/Person/AddressEntity"
import { EStoreServiceType } from "../../Enum/EStoreServiceType"
import { CompanySocialMediaEntity } from "../../Objects/Company/CompanySocialMediaEntity"
import { resolveStoreTimeZone } from "../../utils/timeZone"
import { EDeliveryFulfillmentType } from "../../Enum/EDeliveryFulfillmentType"
import { EFulfillmentMode } from "../../Enum/EFulfillmentMode"
import { EZeroValueSelectionOptionDisplayMode } from "../../Enum/EZeroValueSelectionOptionDisplayMode"
import { normalizeCnpj } from "../../utils/documentFormat"

export const useCreateCompany = () => {
  const [nameFantasy, setNameFantasy] = useState("")

  const [nameSocialReason, setNameSocialReason] = useState("")

  const [cnpj, setCnpj] = useState("")

  const [stateSubscribe, setStateSubscribe] = useState("")

  const [nameMessage, setNameMessage] = useState("")

  const [instagramUrl, setInstagramUrl] = useState("")

  const [facebookUrl, setFacebookUrl] = useState("")

  const [whatsappUrl, setWhatsappUrl] = useState("")

  const [whatsappPhone, setWhatsappPhone] = useState("")

  const [whatsappOrderSupportMessageTemplate, setWhatsappOrderSupportMessageTemplate] = useState("")

  const [tiktokUrl, setTiktokUrl] = useState("")

  const [slug, setSlug] = useState("")

  const [storeServiceType, setStoreServiceType] = useState<EStoreServiceType>(
    EStoreServiceType.Both,
  )

  const [localServiceRadiusMeters, setLocalServiceRadiusMeters] = useState("100")

  const [isFreeShippingEnabled, setIsFreeShippingEnabled] = useState(false)

  const [freeShippingMinimumOrderValue, setFreeShippingMinimumOrderValue] = useState("")

  const [isFixedShippingFeeEnabled, setIsFixedShippingFeeEnabled] = useState(false)

  const [fixedShippingFeeValue, setFixedShippingFeeValue] = useState("")

  const [isDeliveryOrderNotificationEnabled, setIsDeliveryOrderNotificationEnabled] = useState(true)

  const [deliveryFulfillmentType, setDeliveryFulfillmentType] = useState<EDeliveryFulfillmentType>(
    EDeliveryFulfillmentType.Both,
  )

  const [fulfillmentMode, setFulfillmentMode] = useState<EFulfillmentMode>(EFulfillmentMode.Combined)

  const [zeroValueSelectionOptionDisplayMode, setZeroValueSelectionOptionDisplayMode] =
    useState<EZeroValueSelectionOptionDisplayMode>(EZeroValueSelectionOptionDisplayMode.ShowFree)

  const [addressEntity, setAddressEntity] = useState<AddressEntity | null>(null)

  const [company, setCompany] = useState<CompanyEntity | null>(null)

  const [timeZoneId, setTimeZoneId] = useState(
    resolveStoreTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone),
  )

  const handleFreeShippingMinimumOrderValue = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFreeShippingMinimumOrderValue(e.target.value)
  const handleFixedShippingFeeValue = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFixedShippingFeeValue(e.target.value)

  const handleNameFantasyChange = (e: React.ChangeEvent<HTMLInputElement>) => setNameFantasy(e.target.value)
  const handleNameSocialReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => setNameSocialReason(e.target.value)
  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => setCnpj(normalizeCnpj(e.target.value))
  const handleStateSubscribeChange = (e: React.ChangeEvent<HTMLInputElement>) => setStateSubscribe(e.target.value)
  const handleNameMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => setNameMessage(e.target.value)
  const handleInstagramUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => setInstagramUrl(e.target.value)
  const handleFacebookUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => setFacebookUrl(e.target.value)
  const handleWhatsappUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => setWhatsappUrl(e.target.value)
  const handleWhatsappPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => setWhatsappPhone(e.target.value)
  const handleWhatsappOrderSupportMessageTemplateChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setWhatsappOrderSupportMessageTemplate(e.target.value)
  const handleTiktokUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => setTiktokUrl(e.target.value)
  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => setSlug(e.target.value)
  const handleTimeZoneIdChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setTimeZoneId(resolveStoreTimeZone(e.target.value))
  const handleStoreServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setStoreServiceType(e.target.selectedIndex as EStoreServiceType)
  const handleLocalServiceRadiusMetersChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLocalServiceRadiusMeters(e.target.value)

  const handleInitialValue = (myCompany: CompanyEntity | null | undefined) => {
    if (myCompany) {
      setCompany(myCompany)
      setNameFantasy(myCompany.companyName)
      setNameSocialReason(myCompany.socialReason)
      setCnpj(normalizeCnpj(myCompany.cnpj))
      setStateSubscribe(myCompany.stateRegistration)
      setNameMessage(myCompany.companyMessage)
      setInstagramUrl(myCompany.companySocialMediaEntity?.instagramUrl || "")
      setFacebookUrl(myCompany.companySocialMediaEntity?.facebookUrl || "")
      setWhatsappUrl(myCompany.companySocialMediaEntity?.whatsappUrl || "")
      setWhatsappPhone(myCompany.companySocialMediaEntity?.whatsAppPhone || "")
      setWhatsappOrderSupportMessageTemplate(
        myCompany.companySocialMediaEntity?.whatsappOrderSupportMessageTemplate ||
          "Olá! Gostaria de informações do pedido #{{orderId}}.",
      )
      setTiktokUrl(myCompany.companySocialMediaEntity?.tiktokUrl || "")
      setSlug(myCompany.slug)
      setStoreServiceType(myCompany.storeServiceType)
      setLocalServiceRadiusMeters(myCompany.localServiceRadiusMeters.toString())
      setAddressEntity(myCompany.addressEntity)
      setIsFixedShippingFeeEnabled(myCompany.isFixedShippingFeeEnabled)
      setIsFreeShippingEnabled(myCompany.isFreeShippingEnabled)
      setFreeShippingMinimumOrderValue(myCompany.freeShippingMinimumOrderValue.toString())
      setFixedShippingFeeValue(myCompany.fixedShippingFeeValue.toString())
      setIsDeliveryOrderNotificationEnabled(myCompany.isDeliveryOrderNotificationEnabled)
      setDeliveryFulfillmentType(myCompany.deliveryFulfillmentType)
      setFulfillmentMode(myCompany.fulfillmentMode)
      setZeroValueSelectionOptionDisplayMode(myCompany.zeroValueSelectionOptionDisplayMode)
      setTimeZoneId(resolveStoreTimeZone(myCompany.timeZoneId))
    }

    if (freeShippingMinimumOrderValue === "0") {
      setFreeShippingMinimumOrderValue("")
    }

    if (fixedShippingFeeValue === "0") {
      setFixedShippingFeeValue("")
    }
  }

  const handleSubmitForm = () => {
    return new CompanyEntity({
      id: company?.id ?? -1,
      companyName: nameFantasy,
      companyMessage: nameMessage,
      socialReason: nameSocialReason,
      cnpj: normalizeCnpj(cnpj),
      stateRegistration: stateSubscribe,
      companySocialMediaEntity: new CompanySocialMediaEntity({
        instagramUrl: instagramUrl,
        facebookUrl: facebookUrl,
        whatsappUrl: whatsappUrl,
        whatsAppPhone: whatsappPhone,
        whatsappOrderSupportMessageTemplate: whatsappOrderSupportMessageTemplate,
        tiktokUrl: tiktokUrl,
      }),
      slug: slug,
      storeServiceType: storeServiceType,
      localServiceRadiusMeters: localServiceRadiusMeters.toNumber(),
      isFreeShippingEnabled: isFreeShippingEnabled,
      freeShippingMinimumOrderValue: freeShippingMinimumOrderValue.toNumber(),
      isFixedShippingFeeEnabled: isFixedShippingFeeEnabled,
      fixedShippingFeeValue: fixedShippingFeeValue.toNumber(),
      isDeliveryOrderNotificationEnabled: isDeliveryOrderNotificationEnabled,
      deliveryFulfillmentType: deliveryFulfillmentType,
      fulfillmentMode: fulfillmentMode,
      zeroValueSelectionOptionDisplayMode: zeroValueSelectionOptionDisplayMode,
      addressEntity: addressEntity,
      timeZoneId: resolveStoreTimeZone(timeZoneId),
      publicMenuCardLayout: company?.publicMenuCardLayout,
      publicMenuPrimaryColor: company?.publicMenuPrimaryColor,
      publicMenuSecondaryColor: company?.publicMenuSecondaryColor,
      publicMenuAccentColor: company?.publicMenuAccentColor,
      publicMenuBackgroundColor: company?.publicMenuBackgroundColor,
      publicMenuSurfaceColor: company?.publicMenuSurfaceColor,
      publicMenuTextColor: company?.publicMenuTextColor,
      publicMenuBorderColor: company?.publicMenuBorderColor,
      publicMenuButtonColor: company?.publicMenuButtonColor,
      publicMenuButtonTextColor: company?.publicMenuButtonTextColor,
      publicMenuSearchBackgroundColor: company?.publicMenuSearchBackgroundColor,
      publicMenuSearchBorderColor: company?.publicMenuSearchBorderColor,
      publicMenuMobileNavBackgroundColor: company?.publicMenuMobileNavBackgroundColor,
      publicMenuMobileNavActiveBackgroundColor: company?.publicMenuMobileNavActiveBackgroundColor,
      publicMenuMobileNavActiveTextColor: company?.publicMenuMobileNavActiveTextColor,
      publicMenuMobileNavActiveIconColor: company?.publicMenuMobileNavActiveIconColor,
      publicMenuMobileNavInactiveBackgroundColor: company?.publicMenuMobileNavInactiveBackgroundColor,
      publicMenuMobileNavInactiveTextColor: company?.publicMenuMobileNavInactiveTextColor,
      publicMenuMobileNavInactiveIconColor: company?.publicMenuMobileNavInactiveIconColor,
      publicMenuHeaderBackgroundColor: company?.publicMenuHeaderBackgroundColor,
      publicMenuRestaurantNameColor: company?.publicMenuRestaurantNameColor,
      publicMenuRestaurantDescriptionColor: company?.publicMenuRestaurantDescriptionColor,
      publicMenuStatusCardBackgroundColor: company?.publicMenuStatusCardBackgroundColor,
      publicMenuCartButtonColor: company?.publicMenuCartButtonColor,
      publicMenuCartBackgroundColor: company?.publicMenuCartBackgroundColor,
      publicMenuCartBorderColor: company?.publicMenuCartBorderColor,
      publicMenuCartTitleTextColor: company?.publicMenuCartTitleTextColor,
      publicMenuCartDescriptionTextColor: company?.publicMenuCartDescriptionTextColor,
      publicMenuCartTotalLabelTextColor: company?.publicMenuCartTotalLabelTextColor,
      publicMenuCartTotalValueTextColor: company?.publicMenuCartTotalValueTextColor,
      publicMenuEmptyStateTitleColor: company?.publicMenuEmptyStateTitleColor,
      publicMenuEmptyStateDescriptionColor: company?.publicMenuEmptyStateDescriptionColor,
      publicMenuCategoryBackgroundColor: company?.publicMenuCategoryBackgroundColor,
      publicMenuCategoryBorderColor: company?.publicMenuCategoryBorderColor,
      publicMenuCategoryDividerColor: company?.publicMenuCategoryDividerColor,
      publicMenuLoginButtonBackgroundColor: company?.publicMenuLoginButtonBackgroundColor,
      publicMenuLoginButtonTextColor: company?.publicMenuLoginButtonTextColor,
      publicMenuShareButtonBackgroundColor: company?.publicMenuShareButtonBackgroundColor,
      publicMenuShareButtonTextColor: company?.publicMenuShareButtonTextColor,
      publicMenuServiceSelectorBackgroundColor: company?.publicMenuServiceSelectorBackgroundColor,
      publicMenuServiceSelectorTextColor: company?.publicMenuServiceSelectorTextColor,
      publicMenuServiceSelectorActiveBackgroundColor: company?.publicMenuServiceSelectorActiveBackgroundColor,
      publicMenuServiceSelectorActiveTextColor: company?.publicMenuServiceSelectorActiveTextColor,
      publicMenuBorderThickness: company?.publicMenuBorderThickness,
      publicMenuHighlightPromotions: company?.publicMenuHighlightPromotions,
      publicMenuCompactCategories: company?.publicMenuCompactCategories,
      publicMenuUseGradientHeader: company?.publicMenuUseGradientHeader,
      publicMenuShowAwardsTab: company?.publicMenuShowAwardsTab,
      publicMenuShowPromotionsTab: company?.publicMenuShowPromotionsTab,
      publicMenuShowExtraServicesTab: company?.publicMenuShowExtraServicesTab,
      publicMenuTrackingEnabled: company?.publicMenuTrackingEnabled,
      publicMenuGoogleTagManagerId: company?.publicMenuGoogleTagManagerId,
      publicMenuGoogleAnalyticsMeasurementId: company?.publicMenuGoogleAnalyticsMeasurementId,
      publicMenuGoogleAdsConversionId: company?.publicMenuGoogleAdsConversionId,
      publicMenuMetaPixelId: company?.publicMenuMetaPixelId,
    })
  }

  return {
    slug,
    addressEntity,
    storeServiceType,
    localServiceRadiusMeters,
    nameFantasy,
    nameSocialReason,
    cnpj,
    stateSubscribe,
    nameMessage,
    instagramUrl,
    facebookUrl,
    whatsappUrl,
    whatsappPhone,
    whatsappOrderSupportMessageTemplate,
    tiktokUrl,
    timeZoneId,
    isFreeShippingEnabled,
    freeShippingMinimumOrderValue,
    isFixedShippingFeeEnabled,
    fixedShippingFeeValue,
    isDeliveryOrderNotificationEnabled,
    deliveryFulfillmentType,
    fulfillmentMode,
    zeroValueSelectionOptionDisplayMode,
    handleFreeShippingMinimumOrderValue,
    handleFixedShippingFeeValue,
    setAddressEntity,
    setIsFreeShippingEnabled,
    setFreeShippingMinimumOrderValue,
    setIsFixedShippingFeeEnabled,
    setFixedShippingFeeValue,
    setIsDeliveryOrderNotificationEnabled,
    setDeliveryFulfillmentType,
    setFulfillmentMode,
    setZeroValueSelectionOptionDisplayMode,
    handleSlugChange,
    handleTimeZoneIdChange,
    handleStoreServiceTypeChange,
    handleLocalServiceRadiusMetersChange,
    handleNameMessageChange,
    handleInstagramUrlChange,
    handleFacebookUrlChange,
    handleWhatsappUrlChange,
    handleWhatsappPhoneChange,
    handleWhatsappOrderSupportMessageTemplateChange,
    handleTiktokUrlChange,
    handleStateSubscribeChange,
    handleCnpjChange,
    handleNameSocialReasonChange,
    handleNameFantasyChange,
    handleSubmitForm,
    handleInitialValue,
  }
}
